import { httpClient } from '@/src/@core/lib/httpClient'
import {
  Announcement,
  CreateAnnouncementDto,
  UpdateAnnouncementDto,
  CreateInteractionDto,
  AnnouncementResponse,
  SingleAnnouncementResponse
} from '../types/announcementTypes'

const ANNOUNCEMENT_BASE_URL = '/announcements'

export const announcementService = {
  // Get all active announcements from main endpoint
  getActiveAnnouncementsFromMain: async (filters?: {
    type?: string
    priority?: string
    page?: string
    userRole?: string
  }): Promise<Announcement[]> => {
    const params = new URLSearchParams()
    if (filters?.type) params.append('type', filters.type)
    if (filters?.priority) params.append('priority', filters.priority)
    if (filters?.page) params.append('page', filters.page)
    if (filters?.userRole) params.append('userRole', filters.userRole)

    const queryString = params.toString()
    const url = queryString ? `${ANNOUNCEMENT_BASE_URL}?${queryString}` : ANNOUNCEMENT_BASE_URL

    const response = await httpClient.get<{ data: { data: Announcement[]; _metadata: any } }>(url)

    // Handle the actual API response structure: response.data.data.data
    const announcements = response.data.data?.data || []

    // Convert integer booleans to actual booleans
    if (Array.isArray(announcements)) {
      return announcements.map(announcement => ({
        ...announcement,
        isActive: Boolean(announcement.isActive),
        isDismissible: Boolean(announcement.isDismissible),
        isCollapsible: Boolean(announcement.isCollapsible),
        requiresAcknowledgment: Boolean(announcement.requiresAcknowledgment)
      }))
    } else {
      // If no data or unexpected format, return empty array
      return []
    }
  },

  // Get active announcements with user interactions
  getActiveAnnouncements: async (userId: number, page?: number, limit?: number): Promise<Announcement[]> => {
    let url = `${ANNOUNCEMENT_BASE_URL}/user/${userId}`

    // Only add pagination parameters if they are provided
    if (page !== undefined && limit !== undefined) {
      url += `?page=${page}&limit=${limit}`
    }

    const response = await httpClient.get<{ data: { data: { announcements: Announcement[]; pagination: any } } }>(url)

    // Handle the actual API response structure: response.data.data.data.announcements
    const announcements = response.data.data?.data?.announcements || []

    // Convert integer booleans to actual booleans
    if (Array.isArray(announcements)) {
      return announcements.map(announcement => ({
        ...announcement,
        isActive: Boolean(announcement.isActive),
        isDismissible: Boolean(announcement.isDismissible),
        isCollapsible: Boolean(announcement.isCollapsible),
        requiresAcknowledgment: Boolean(announcement.requiresAcknowledgment)
      }))
    } else {
      return []
    }
  },

  // Get expired announcements
  getExpiredAnnouncements: async (page = 1, limit = 10): Promise<Announcement[]> => {
    const url = `${ANNOUNCEMENT_BASE_URL}/expired?page=${page}&limit=${limit}`
    const response = await httpClient.get<{ data: { data: { announcements: Announcement[]; pagination: any } } }>(url)

    // Handle the actual API response structure: response.data.data.data.announcements
    const announcements = response.data.data?.data?.announcements || []

    // Convert integer booleans to actual booleans
    if (Array.isArray(announcements)) {
      return announcements.map(announcement => ({
        ...announcement,
        isActive: Boolean(announcement.isActive),
        isDismissible: Boolean(announcement.isDismissible),
        isCollapsible: Boolean(announcement.isCollapsible),
        requiresAcknowledgment: Boolean(announcement.requiresAcknowledgment)
      }))
    } else {
      return []
    }
  },

  // Get user-specific announcements (keeping for backward compatibility)
  getUserAnnouncements: async (userId: number, pagePath?: string): Promise<Announcement[]> => {
    const params = pagePath ? `?page=${pagePath}` : ''
    const response = await httpClient.get<AnnouncementResponse>(`${ANNOUNCEMENT_BASE_URL}/user/${userId}${params}`)

    return response.data.data.data || []
  },

  // Get all announcements (admin)
  getAllAnnouncements: async (filters?: {
    type?: string
    priority?: string
    page?: string
    userRole?: string
  }): Promise<Announcement[]> => {
    const params = new URLSearchParams()
    if (filters?.type) params.append('type', filters.type)
    if (filters?.priority) params.append('priority', filters.priority)
    if (filters?.page) params.append('page', filters.page)
    if (filters?.userRole) params.append('userRole', filters.userRole)

    const queryString = params.toString()
    const url = queryString ? `${ANNOUNCEMENT_BASE_URL}?${queryString}` : ANNOUNCEMENT_BASE_URL

    const response = await httpClient.get<AnnouncementResponse>(url)

    // Handle the actual API response structure: response.data.data.data (nested)
    const announcements = (response.data as any).data?.data || response.data.data || []

    // Convert integer booleans to actual booleans
    if (Array.isArray(announcements)) {
      return announcements.map(announcement => ({
        ...announcement,
        isActive: Boolean(announcement.isActive),
        isDismissible: Boolean(announcement.isDismissible),
        isCollapsible: Boolean(announcement.isCollapsible),
        requiresAcknowledgment: Boolean(announcement.requiresAcknowledgment)
      }))
    } else {
      // If no data or unexpected format, return empty array
      return []
    }
  },

  // Get single announcement
  getAnnouncement: async (id: number): Promise<Announcement> => {
    const response = await httpClient.get<SingleAnnouncementResponse>(`${ANNOUNCEMENT_BASE_URL}/${id}`)

    return response.data.data
  },

  // Create announcement
  createAnnouncement: async (data: CreateAnnouncementDto): Promise<Announcement> => {
    const response = await httpClient.post<SingleAnnouncementResponse>(ANNOUNCEMENT_BASE_URL, data)

    return response.data.data
  },

  // Update announcement
  updateAnnouncement: async (id: number, data: UpdateAnnouncementDto): Promise<Announcement> => {
    const response = await httpClient.patch<SingleAnnouncementResponse>(`${ANNOUNCEMENT_BASE_URL}/${id}`, data)

    return response.data.data
  },

  // Deactivate announcement using dedicated deactivate endpoint
  deactivateAnnouncement: async (id: number): Promise<Announcement> => {
    const response = await httpClient.patch<SingleAnnouncementResponse>(`${ANNOUNCEMENT_BASE_URL}/deactivate/${id}`)

    return response.data.data
  },

  // Delete announcement
  deleteAnnouncement: async (id: number): Promise<void> => {
    await httpClient.delete(`${ANNOUNCEMENT_BASE_URL}/${id}`)
  },

  // Record user interaction
  recordInteraction: async (announcementId: number, interactionData: CreateInteractionDto): Promise<void> => {
    await httpClient.post(`${ANNOUNCEMENT_BASE_URL}/${announcementId}/interactions`, interactionData)
  },

  // Expire announcements (admin)
  expireAnnouncements: async (): Promise<void> => {
    await httpClient.post(`${ANNOUNCEMENT_BASE_URL}/expire`)
  }
}
